<template>
  <div class="cohort_root">
    <!-- Date range controls -->
    <div class="cohort_controls">
      <div class="date_presets">
        <Button
          v-for="p in [{ key: '7d', label: '7d' }, { key: '30d', label: '30d' }, { key: '90d', label: '90d' }, { key: 'all', label: 'All time' }]"
          :key="p.key"
          :label="p.label"
          size="small"
          :severity="activePreset === p.key ? 'primary' : 'secondary'"
          :outlined="activePreset !== p.key"
          :disabled="isLoading"
          rounded
          @click="applyPreset(p.key)"
        />
      </div>
      <div class="date_range">
        <label class="date_label">From</label>
        <input v-model="dateFrom" type="date" class="date_input" @change="activePreset = ''; fetchStats()" />
        <label class="date_label">To</label>
        <input v-model="dateTo" type="date" class="date_input" @change="activePreset = ''; fetchStats()" />
      </div>
      <Button label="Export CSV" severity="success" size="small" outlined :disabled="isLoading" @click="exportCsv" />
    </div>

    <!-- Loading state -->
    <div v-if="isLoading" class="state_block">Loading cohort data…</div>

    <!-- No data -->
    <div v-else-if="!stats" class="state_block">No cohort data available.</div>

    <!-- Rate tiles -->
    <template v-else>
      <div class="rate_grid">
        <div class="rate_tile">
          <div :class="['rate_value', rateBadgeClass(signupRate)]">{{ signupRate !== null ? signupRate + '%' : '—' }}</div>
          <div class="rate_label">
            <span class="step-hint-wrap">
              Signup rate
              <span class="step-hint-bubble">Share of link visitors who completed registration (traffic → registration)</span>
            </span>
          </div>
        </div>
        <div class="rate_tile">
          <div :class="['rate_value', rateBadgeClass(coreRate)]">{{ coreRate !== null ? coreRate + '%' : '—' }}</div>
          <div class="rate_label">
            <span class="step-hint-wrap">
              Core action rate
              <span class="step-hint-bubble">Share of link visitors who downloaded at least one free asset (traffic → download)</span>
            </span>
          </div>
        </div>
        <div class="rate_tile">
          <div :class="['rate_value', rateBadgeClass(ret30Rate)]">{{ ret30Rate !== null ? ret30Rate + '%' : '—' }}</div>
          <div class="rate_label">
            <span class="step-hint-wrap">
              Ret. 30d rate
              <span class="step-hint-bubble">Share of link visitors who returned within 30 days of registration</span>
            </span>
          </div>
        </div>
      </div>

      <!-- Engagement distribution bar -->
      <div class="engagement_section">
        <div class="engagement_title">Engagement distribution</div>
        <div class="engagement_bar">
          <div
            class="engagement_segment seg_active"
            :style="{ width: stats.pct_active + '%' }"
            :title="`Active: ${stats.pct_active}%`"
          />
          <div
            class="engagement_segment seg_engaged"
            :style="{ width: stats.pct_engaged + '%' }"
            :title="`Engaged: ${stats.pct_engaged}%`"
          />
          <div
            class="engagement_segment seg_dormant"
            :style="{ width: stats.pct_dormant + '%' }"
            :title="`Dormant: ${stats.pct_dormant}%`"
          />
        </div>
        <div class="engagement_legend">
          <span class="legend_dot dot_active" />
          <span class="step-hint-wrap">
            Active {{ stats.pct_active }}%
            <span class="step-hint-bubble">Users with high engagement: regularly active or power users (regular_user, power_user tiers)</span>
          </span>
          <span class="legend_dot dot_engaged" />
          <span class="step-hint-wrap">
            Engaged {{ stats.pct_engaged }}%
            <span class="step-hint-bubble">Users with moderate engagement: visit weekly or occasionally (weekly_user, casual_visitor tiers)</span>
          </span>
          <span class="legend_dot dot_dormant" />
          <span class="step-hint-wrap">
            Dormant {{ stats.pct_dormant }}%
            <span class="step-hint-bubble">Users with no meaningful activity: registered but never returned or visited only once (never_active, one_day_visitor tiers)</span>
          </span>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import Button from 'primevue/button';

export default {
  name: 'PartnerCohortStats',

  components: { Button },

  props: {
    partnerId: {
      type: Number,
      required: true,
    },
  },

  data() {
    const today = new Date();
    const weekAgo = new Date(today);
    weekAgo.setDate(today.getDate() - 7);
    const fmt = (d) => d.toISOString().slice(0, 10);
    return {
      dateFrom: fmt(weekAgo),
      dateTo: fmt(today),
      activePreset: '7d',
    };
  },

  computed: {
    ...mapGetters(['COHORT_STATS', 'IS_COHORT_LOADING', 'AGGREGATE_FUNNEL']),
    stats() {
      return this.COHORT_STATS;
    },
    isLoading() {
      return this.IS_COHORT_LOADING;
    },
    signupRate() {
      return this._aggregatePct('signup');
    },
    coreRate() {
      return this._aggregatePct('core_action');
    },
    ret30Rate() {
      return this._aggregatePct('retention_30d');
    },
  },

  mounted() {
    this.fetchStats();
    this.loadAggregate();
  },

  methods: {
    async fetchStats() {
      const params = {};
      if (this.dateFrom) params.date_from = this.dateFrom;
      if (this.dateTo) params.date_to = this.dateTo;
      await this.$store.dispatch('loadPartnerCohortStats', {
        partnerId: this.partnerId,
        params,
      });
    },

    async loadAggregate() {
      // Ensure links are loaded before loading funnels
      await this.$store.dispatch('loadPartnerLinks', this.partnerId);
      await this.$store.dispatch('loadAllLinkFunnels', { partnerId: this.partnerId });
      console.debug(
        `[PartnerCohortStats] rate tiles signup=${this.signupRate}% core=${this.coreRate}% ret30=${this.ret30Rate}%`,
      );
    },

    _aggregatePct(stepName) {
      const agg = this.AGGREGATE_FUNNEL;
      if (!agg) return null;
      const traffic = agg.steps.find((s) => s.name === 'traffic');
      const step = agg.steps.find((s) => s.name === stepName);
      if (!traffic || !step || !traffic.count) return null;
      return Math.round((step.count / traffic.count) * 100);
    },

    rateBadgeClass(pct) {
      if (pct === null) return 'rate_neutral';
      if (pct >= 40) return 'rate_green';
      if (pct >= 20) return 'rate_amber';
      return 'rate_red';
    },

    resetDateRange() {
      this.dateFrom = '';
      this.dateTo = '';
      this.activePreset = 'all';
      this.fetchStats();
    },

    applyPreset(preset) {
      this.activePreset = preset;
      const today = new Date();
      const fmt = (d) => d.toISOString().slice(0, 10);
      if (preset === 'all') {
        this.dateFrom = '';
        this.dateTo = '';
      } else {
        const days = { '7d': 7, '30d': 30, '90d': 90 }[preset];
        const from = new Date(today);
        from.setDate(today.getDate() - days);
        this.dateFrom = fmt(from);
        this.dateTo = fmt(today);
      }
      this.fetchStats();
    },

    async exportCsv() {
      const params = {};
      if (this.dateFrom) params.date_from = this.dateFrom;
      if (this.dateTo) params.date_to = this.dateTo;
      await this.$store.dispatch('exportPartnerCohortCsv', {
        partnerId: this.partnerId,
        params,
      });
    },

    formatDate(iso) {
      if (!iso) return '—';
      return iso.slice(0, 10);
    },
  },
};
</script>

<style scoped>
.cohort_root {
  padding: 16px;
}

/* Controls */
.cohort_controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.date_range {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.date_presets {
  display: flex;
  gap: 4px;
}

.btn_preset {
  padding: 4px 12px;
  border: 1px solid var(--border-color);
  border-radius: 20px;
  background: transparent;
  color: var(--text-color-secondary);
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.btn_preset:hover:not(:disabled) {
  border-color: #3b82f6;
  color: #3b82f6;
}

.btn_preset__active {
  background: #3b82f6;
  border-color: #3b82f6;
  color: #fff !important;
}

.btn_preset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.date_label {
  font-size: 12px;
  color: var(--text-color-secondary);
}

.date_input {
  padding: 4px 8px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  font-size: 12px;
  background: var(--background-primary);
  color: var(--text-color);
}

.btn_apply {
  padding: 5px 14px;
  border: 1px solid #3b82f6;
  border-radius: 5px;
  background: #3b82f6;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}

.btn_apply:hover:not(:disabled) {
  opacity: 0.85;
}

.btn_apply:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn_reset {
  padding: 5px 14px;
  border: 1px solid var(--border-color);
  border-radius: 5px;
  background: transparent;
  color: var(--text-color-secondary);
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.btn_reset:hover:not(:disabled) {
  background: var(--background-secondary);
}

.btn_reset:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn_export {
  padding: 5px 14px;
  border: 1px solid #10b981;
  border-radius: 5px;
  background: transparent;
  color: #10b981;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
}

.btn_export:hover:not(:disabled) {
  background: #10b981;
  color: #fff;
}

.btn_export:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* State */
.state_block {
  padding: 40px 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-color-secondary);
}

/* Rate tiles */
.rate_grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.rate_tile {
  padding: 14px 16px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--background-secondary);
  text-align: center;
}

.rate_value {
  font-size: 24px;
  font-weight: 700;
  line-height: 1.2;
  padding: 4px 10px;
  border-radius: 6px;
  display: inline-block;
}

.rate_green  { background: #dcfce7; color: #166534; }
.rate_amber  { background: #fef9c3; color: #854d0e; }
.rate_red    { background: #fee2e2; color: #991b1b; }
.rate_neutral { background: #f3f4f6; color: #6b7280; }

.rate_label {
  font-size: 11px;
  color: var(--text-color-secondary);
  text-transform: uppercase;
  letter-spacing: 0.4px;
  margin-top: 6px;
}

/* Tooltips */
.step-hint-wrap {
  position: relative;
  cursor: default;
  border-bottom: 1px dotted #94a3b8;
  display: inline;
}
.step-hint-bubble {
  position: absolute;
  bottom: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  width: 220px;
  background: #1e293b;
  color: #f1f5f9;
  font-size: 11px;
  line-height: 1.5;
  padding: 6px 8px;
  border-radius: 6px;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
  white-space: normal;
  font-weight: 400;
  text-transform: none;
}
.step-hint-bubble::after {
  content: '';
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border: 5px solid transparent;
  border-top-color: #1e293b;
}
.step-hint-wrap:hover .step-hint-bubble {
  opacity: 1;
}

/* Engagement bar */
.engagement_section {
  margin-top: 8px;
}

.engagement_title {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: var(--text-color-secondary);
  margin-bottom: 8px;
}

.engagement_bar {
  display: flex;
  height: 14px;
  border-radius: 7px;
  overflow: hidden;
  background: var(--background-secondary);
}

.engagement_segment {
  height: 100%;
  transition: width 0.3s ease;
}

.seg_active  { background: #10b981; }
.seg_engaged { background: #3b82f6; }
.seg_dormant { background: #6b7280; }

.engagement_legend {
  display: flex;
  gap: 16px;
  align-items: center;
  margin-top: 8px;
  font-size: 12px;
  color: var(--text-color-secondary);
  flex-wrap: wrap;
}

.legend_dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 4px;
}

.dot_active  { background: #10b981; }
.dot_engaged { background: #3b82f6; }
.dot_dormant { background: #6b7280; }
</style>
